<template>
  <gg-card>
    <gg-card-header>
      <span class="mr-2">Manage AutoSSL</span>
      <gg-btn @click="loadDisabledDomains"
              :loading="loading"
              aria-label="Refresh Auto SSL domains list."
              class="mr-2">
        <v-icon>mdi-refresh</v-icon>
      </gg-btn>
      <start-auto-ssl />
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-icon color="primary" class="ml-2" small v-on="on">
            mdi-help-circle
          </v-icon>
        </template>
        <span>Enable or disable automatic Let's Encrypt SSL certificates by domain.</span>
      </v-tooltip>
    </gg-card-header>
    <gg-card-text>
      <v-data-table :headers="headers"
                    :loading="loading || processing"
                    :items="sslDomainsList">
        <template #item.autoSslDisabled="{ item }">
          <span v-if="item.autoSslDisabled && item.type === 'letsencrypt'" style="color:red;font-weight:bold;">Disabled</span>
          <div v-else-if="item.type === 'letsencrypt'">
            <div class="primary--text d-flex align-center flex-column" style="max-width: fit-content;">
              <img class="mb-1" src="/static/img/amui/le-logo-lockonly.png" width="28px" alt="Let's Encrypt Enabled"/>
              <b>Enabled</b>
            </div>
          </div>
          <div v-else-if="item.type === 'alphassl'" class="d-flex align-center flex-column" style="max-width: fit-content;">
            <img src="/static/img/amui/globalsign-logo-eye.png" width="24px" alt="GlobalSign">
            <b class="primary--text">Globalsign</b>
          </div>
        </template>
        <template #item.manage="{ item }">
          <v-switch v-show="item.type === 'letsencrypt'"
                    label=""
                    color="primary"
                    :input-value="!item.autoSslDisabled"
                    :true-value="true"
                    :false-value="false"
                    @click="changeAutoSsl(item.domain, !item.autoSslDisabled)"
                    :disabled="processing || loading" />
        </template>
        <template #item.actions="{ item }">
          <gg-btn v-if="item.type === 'letsencrypt'"
           :to="{ name: 'hosting_ssl_create', query: { d: item.domain, sid: hosting.id } }">
           Upgrade
          </gg-btn>
          <gg-btn v-else
           :to="{ name: 'hosting_ssl_manage', params: { id: item.sslId } }">
           Manage
          </gg-btn>
        </template>
      </v-data-table>
    </gg-card-text>
  </gg-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import amapi from '@/api/amapi'
import StartAutoSsl from '@/components/Services/Hosting/Shared/Ssl_manager/StartAutoSsl'

export default {
  name: 'AutoSslManage',
  components: {
    StartAutoSsl
  },
  data () {
    return {
      processing: false,
      headers: [
        {
          text: 'Domain',
          value: 'domain'
        },
        {
          text: 'Status',
          value: 'autoSslDisabled'
        },
        {
          text: 'Toggle',
          sortable: false,
          value: 'manage'
        },
        {
          text: 'Manage',
          value: 'actions',
          sortable: false
        }
      ]
    }
  },
  computed: {
    ...mapGetters({
      hosting: 'hosting',
      hostingDomainsList: 'hostingDomainsList',
      hostingSubdomainsList: 'hostingSubdomainsList',
      autoSslServiceId: 'auto_ssl_manager/autoSslServiceId',
      autoSslDisabledDomains: 'auto_ssl_manager/autoSslDisabledDomains',
      loading: 'auto_ssl_manager/loadingAutoSslDisabledDomains',
      services: 'services',
    }),
    sslDomainsList () {
      const alphaSSL = this.services.filter(service => service.type === 'ssl' && service.related_service === this.hosting.id)
        .map(service => {
          return {
            domain: service.domain,
            autoSslDisabled: false,
            type: 'alphassl',
            sslId: service.ssl_id,
          }
        })

      const leSSL = [
        ...this.hostingDomainsList,
        ...this.hostingSubdomainsList
      ].map(domain => ({
        domain,
        autoSslDisabled: !!this.autoSslDisabledDomains.find(sslDomain => sslDomain.excluded_domain == domain),
        type: 'letsencrypt',
      }))

      return alphaSSL.concat(leSSL)
    }
  },
  methods: {
    ...mapActions({
      getDisabledDomains: 'auto_ssl_manager/getDisabledDomains',
      reportError: 'reportError',
      reportSuccess: 'reportSuccess'
    }),
    async loadDisabledDomains () {
      try {
        await this.getDisabledDomains({ serviceId: this.hosting.id, username: this.$route.query.user })
      } catch (e) {
        this.reportError(e)
      }
    },
    async disableAutoSsl (domain) {
      this.processing = true
      const data = {
        id: this.hosting.id,
        params: {
          uapi_module: 'SSL',
          uapi_function: 'add_autossl_excluded_domains',
          domains: domain
        },
        username: this.$route.query.user,
      }
      try {
        await amapi.makecall('cpanel_uapi', data)
        this.reportSuccess(`AutoSSL for "${domain}" disabled.`)
      } catch (e) {
        this.reportError(e)
      } finally {
        this.processing = false
        this.loadDisabledDomains()
      }
    },
    async enableAutoSsl (domain) {
      this.processing = true
      const data = {
        id: this.hosting.id,
        params: {
          uapi_module: 'SSL',
          uapi_function: 'remove_autossl_excluded_domains',
          domains: domain
        },
        username: this.$route.query.user
      }
      try {
        await amapi.makecall('cpanel_uapi', data)
        this.reportSuccess(`AutoSSL for "${domain}" enabled.`)
      } catch (e) {
        this.reportError(e)
      } finally {
        this.processing = false
        this.loadDisabledDomains()
      }
    },
    changeAutoSsl (domain, enabled) {
      if (enabled) {
        this.disableAutoSsl(domain)
      } else {
        this.enableAutoSsl(domain)
      }
    },
  },
  mounted () {
    if (this.autoSslServiceId == this.hosting.id && this.autoSslDisabledDomains.length > 0) {
      return
    }
    this.loadDisabledDomains()
  }
}
</script>
